<template>
  <div class="outter-modal">
    <div class="modal fade show" style="display: block; overflow: scroll" tabindex="-1">
      <div class="modal-dialog modal-md modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <span>Undispatch Reason</span>
            <button @click="onClose" type="button" class="close" aria-label="Close">×</button>
          </div>

          <div v-if="step === 1" class="modal-body">
            <p class="mb-3">Why do you want to undispatch this order?</p>
            <div class="responsibility-grid">
              <button
                v-for="opt in options"
                :key="opt.value"
                type="button"
                class="responsibility-card"
                :class="{ 'is-selected': selected === opt.value }"
                @click="selected = opt.value"
              >
                <div class="responsibility-icon" :style="{ color: opt.color }"><i :class="opt.icon"></i></div>
                <div class="responsibility-title">{{ opt.title }}</div>
                <div class="responsibility-desc">{{ opt.description }}</div>
              </button>
            </div>
          </div>

          <div v-if="step === 2" class="modal-body">
            <p class="mb-2"><strong>{{ selectedOption.title }}</strong></p>
            <p class="text-muted small mb-3">{{ selectedOption.description }}</p>

            <div class="form-group">
              <label for="undispatch-reason">Short reason <span class="text-danger">*</span></label>
              <input
                id="undispatch-reason"
                v-model="reason"
                type="text"
                class="form-control"
                maxlength="500"
                placeholder="e.g. truck broke down, customer cancelled..."
              />
              <small v-if="errors.reason" class="text-danger">{{ errors.reason }}</small>
            </div>

            <div class="form-group">
              <label for="undispatch-notes">Notes (optional)</label>
              <textarea
                id="undispatch-notes"
                v-model="notes"
                class="form-control"
                rows="3"
                placeholder="Additional context for manager review"
              />
            </div>

            <div v-if="errors.general" class="alert alert-danger mt-2">{{ errors.general }}</div>
          </div>

          <div class="modal-footer">
            <button v-if="step === 1" type="button" class="btn btn-secondary" @click="onClose">Cancel</button>
            <button
              v-if="step === 1"
              type="button"
              class="btn btn-primary"
              :disabled="!selected"
              @click="step = 2"
            >Continue</button>

            <button v-if="step === 2" type="button" class="btn btn-secondary" @click="step = 1">Back</button>
            <button
              v-if="step === 2"
              type="button"
              class="btn btn-primary"
              :disabled="submitting || !reason.trim()"
              @click="submit"
            >{{ submitting ? 'Working...' : 'Confirm Undispatch' }}</button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop" />
  </div>
</template>

<script>
export default {
  name: 'UndispatchReasonModal',
  props: {
    onClose: {
      type: Function,
      default: () => {},
    },
    submitting: {
      type: Boolean,
      default: false,
    },
    serverError: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      step: 1,
      selected: null,
      reason: '',
      notes: '',
      options: [
        {
          value: 'carrier_fault',
          icon: 'fas fa-truck',
          color: '#e15554',
          title: 'Carrier Fault',
          description: 'The carrier is at fault for not being able to transport this order.',
        },
        {
          value: 'broker_company_fault',
          icon: 'fas fa-building',
          color: '#3f88c5',
          title: 'Broker / Company Fault',
          description: 'The issue is due to an error or issue on our end.',
        },
        {
          value: 'customer_shipper_fault',
          icon: 'fas fa-user',
          color: '#8e5ad6',
          title: 'Customer / Shipper Fault',
          description: 'The customer or shipper is at fault for this issue.',
        },
        {
          value: 'mutual_agreement',
          icon: 'fas fa-handshake',
          color: '#2bb673',
          title: 'Mutual Agreement',
          description: 'Both parties have agreed to undispatch this order.',
        },
        {
          value: 'admin_mistake',
          icon: 'fas fa-exclamation-triangle',
          color: '#e2a03f',
          title: 'Admin Mistake',
          description: 'Order was dispatched by mistake or to the wrong carrier.',
        },
        {
          value: 'unknown_review',
          icon: 'fas fa-question-circle',
          color: '#7a8a9a',
          title: 'Unknown / Needs Review',
          description: 'Reason is unclear - flag for manager review later.',
        },
      ],
    };
  },
  computed: {
    selectedOption() {
      return this.options.find(o => o.value === this.selected) || {};
    },
    errors() {
      const e = {};
      if (this.step === 2 && !this.reason.trim()) e.reason = 'Reason is required';
      if (this.serverError) e.general = this.serverError;
      return e;
    },
  },
  watch: {
    selected(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.reason = '';
        this.notes = '';
      }
    },
  },
  methods: {
    submit() {
      if (!this.selected || !this.reason.trim()) return;
      this.$emit('confirm', {
        responsibility: this.selected,
        reason: this.reason.trim(),
        notes: this.notes.trim() || null,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.outter-modal {
  position: absolute;
  z-index: 1089;
}

.responsibility-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.responsibility-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
  padding: 0.75rem;
  border: 2px solid #d8dde3;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s, box-shadow 0.15s, background-color 0.15s;
}

.responsibility-card:hover {
  border-color: #5b8def;
}

.responsibility-card.is-selected {
  border-color: #2c6cf6;
  background: #eaf1ff;
  box-shadow: 0 0 0 3px rgba(44, 108, 246, 0.35);
}

.responsibility-card.is-selected .responsibility-title {
  color: #1a4fc4;
}

.responsibility-icon {
  font-size: 1.5rem;
  line-height: 1;
}

.responsibility-title {
  font-weight: 600;
  font-size: 0.95rem;
}

.responsibility-desc {
  font-size: 0.8rem;
  color: #6b7280;
}
</style>

<style>
.dark-style .responsibility-card {
  background: #2b2e36;
  border-color: #3f434d;
  color: #e6e8ec;
}

.dark-style .responsibility-card:hover {
  border-color: #5b8def;
}

.dark-style .responsibility-card.is-selected {
  border-color: #5b8def;
  background: #1e3461;
  box-shadow: 0 0 0 3px rgba(91, 141, 239, 0.45);
}

.dark-style .responsibility-card.is-selected .responsibility-title {
  color: #ffffff;
}

.dark-style .responsibility-card.is-selected .responsibility-desc {
  color: #cdd6e3;
}

.dark-style .responsibility-desc {
  color: #9aa3b2;
}
</style>
